import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  CheckCircle, 
  AlertCircle,
  FileText,
  HardDrive,
  Database,
  Search,
  RefreshCw
} from 'lucide-react';
import { api } from '../api';
import type { SyncStatus as SyncStatusType } from '../types';

export function SyncStatus() {
  const navigate = useNavigate();
  const [syncStatus, setSyncStatus] = useState<SyncStatusType | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    loadSyncStatus();
    const interval = setInterval(loadSyncStatus, 60000); // Refresh every minute
    return () => clearInterval(interval);
  }, []);

  const loadSyncStatus = async () => {
    try {
      const data = await api.getSyncStatus();
      setSyncStatus(data);
    } catch (error) {
      console.error('Failed to load sync status:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    return status === 'healthy' || status === 'ok' || status === 'active'
      ? <CheckCircle size={20} color="var(--doyle-green)" />
      : <AlertCircle size={20} color="var(--doyle-red)" />;
  };

  if (isLoading) {
    return (
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        height: '100vh' 
      }}>
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    );
  }

  return (
    <div style={{ 
      minHeight: '100vh', 
      background: 'var(--bg-primary)',
      padding: 'var(--spacing-lg)'
    }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ 
          display: 'flex', 
          alignItems: 'center', 
          justifyContent: 'space-between',
          marginBottom: 'var(--spacing-lg)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-md)' }}>
            <button 
              className="btn-icon btn-ghost"
              onClick={() => navigate('/chat')}
            >
              <ArrowLeft size={20} />
            </button>
            <h1>Document Sync Status</h1>
          </div>
          <button 
            className="btn-secondary"
            onClick={loadSyncStatus}
          >
            <RefreshCw size={16} />
            Refresh
          </button>
        </div>

        {syncStatus && (
          <>
            {/* Overview Cards */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: 'var(--spacing-md)',
              marginBottom: 'var(--spacing-lg)'
            }}>
              <div className="card">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 'var(--spacing-md)',
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 'var(--radius-md)',
                    background: 'linear-gradient(135deg, var(--doyle-blue) 0%, var(--doyle-purple) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <FileText size={24} color="white" />
                  </div>
                  <div>
                    <div style={{ fontSize: '2rem', fontWeight: 700 }}>
                      {syncStatus.documents_indexed}
                    </div>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: 'var(--text-secondary)' 
                    }}>
                      Documents Indexed
                    </div>
                  </div>
                </div>
              </div>

              <div className="card">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 'var(--spacing-md)',
                  marginBottom: 'var(--spacing-sm)'
                }}>
                  <div style={{
                    width: 48,
                    height: 48,
                    borderRadius: 'var(--radius-md)',
                    background: 'linear-gradient(135deg, var(--doyle-green) 0%, var(--doyle-blue) 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <RefreshCw size={24} color="white" />
                  </div>
                  <div>
                    <div style={{ fontSize: '1.25rem', fontWeight: 600 }}>
                      {new Date(syncStatus.last_sync).toLocaleString()}
                    </div>
                    <div style={{ 
                      fontSize: '0.875rem', 
                      color: 'var(--text-secondary)' 
                    }}>
                      Last Sync
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Component Status */}
            <div style={{ 
              display: 'grid', 
              gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
              gap: 'var(--spacing-md)'
            }}>
              <div className="card">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: 'var(--spacing-md)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                    <HardDrive size={20} color="var(--doyle-blue)" />
                    <h3 style={{ fontSize: '1rem' }}>File Share</h3>
                  </div>
                  {getStatusIcon(syncStatus.file_share_status)}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Status: {syncStatus.file_share_status}
                </p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--spacing-xs)' }}>
                  Azure File Sync - knowledge-sync
                </p>
              </div>

              <div className="card">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: 'var(--spacing-md)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                    <Database size={20} color="var(--doyle-blue)" />
                    <h3 style={{ fontSize: '1rem' }}>Blob Storage</h3>
                  </div>
                  {getStatusIcon(syncStatus.blob_storage_status)}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Status: {syncStatus.blob_storage_status}
                </p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--spacing-xs)' }}>
                  documents container
                </p>
              </div>

              <div className="card">
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'space-between',
                  marginBottom: 'var(--spacing-md)'
                }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-sm)' }}>
                    <Search size={20} color="var(--doyle-blue)" />
                    <h3 style={{ fontSize: '1rem' }}>Search Index</h3>
                  </div>
                  {getStatusIcon(syncStatus.search_index_status)}
                </div>
                <p style={{ fontSize: '0.875rem', color: 'var(--text-secondary)' }}>
                  Status: {syncStatus.search_index_status}
                </p>
                <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginTop: 'var(--spacing-xs)' }}>
                  devon-knowledge-index
                </p>
              </div>
            </div>

            {/* Sync Pipeline Info */}
            <div className="card" style={{ marginTop: 'var(--spacing-lg)' }}>
              <h3 style={{ marginBottom: 'var(--spacing-md)', fontSize: '1rem' }}>
                Data Sync Pipeline
              </h3>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: 'var(--spacing-sm)',
                flexWrap: 'wrap',
                fontSize: '0.875rem',
                color: 'var(--text-secondary)'
              }}>
                <span style={{ fontWeight: 600 }}>D:\DevGPT (On-Prem)</span>
                <span>→</span>
                <span>Azure File Sync (~10 min)</span>
                <span>→</span>
                <span style={{ fontWeight: 600 }}>knowledge-sync (File Share)</span>
                <span>→</span>
                <span>ADF Pipeline (Hourly)</span>
                <span>→</span>
                <span style={{ fontWeight: 600 }}>documents (Blob)</span>
                <span>→</span>
                <span>Cognitive Search (Hourly)</span>
                <span>→</span>
                <span style={{ fontWeight: 600 }}>devon-knowledge-index</span>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
